import type { ArtefactType } from '@heidi/evidence/contracts';
import { logger } from '@heidi/runtime/logger';
import type { IntlShape, MessageDescriptor } from 'react-intl';

import type { ToolUseItem } from '../../../../../contracts/harness.types';
import { getWorkHostPort } from '../../../../work-home/work-home-ports';

export const resolveExportDocumentFilename = (payload: {
  filename?: string;
  title?: string;
  url?: string;
}): string => getWorkHostPort('evidence').resolveExportDocumentFilename(payload);

import { renderArtefactToolUse, type ArtefactPayload } from './artefact-tool';
import { readStandardExportPayload } from './readers';

import type { JSX } from 'react';

export type OpenExportDocumentPayload = {
  url: string;
  filename: string;
  title: string;
  /** Force `.pdf` extension for preview routing (filled forms). */
  forcePdfExtension?: boolean;
};

/**
 * Opens generated exports in the artifact canvas instead of a new tab.
 * Only wired on surfaces that pass `onOpenDocumentExport`.
 */
export const buildOnOpenExportDocumentPreview =
  (onOpen?: (payload: OpenExportDocumentPayload) => void) =>
  (payload: ArtefactPayload): (() => void) | undefined => {
    if (!onOpen || !payload.url) return undefined;
    const filename = resolveExportDocumentFilename(payload);
    const title = payload.title?.trim() || filename;
    return () => onOpen({ url: payload.url!, filename, title });
  };

export interface StandardExportToolConfig {
  artefactType: ArtefactType;
  titleFallback: MessageDescriptor;
  readPayload?: (item: ToolUseItem) => ArtefactPayload;
  buildOnPreview?: (payload: ArtefactPayload) => (() => void) | undefined;
}

export const renderStandardExportToolUse = (
  item: ToolUseItem,
  intl: IntlShape,
  config: StandardExportToolConfig,
  onOpenDocumentExport?: (payload: OpenExportDocumentPayload) => void
): JSX.Element | null =>
  renderArtefactToolUse(
    item,
    {
      artefactType: config.artefactType,
      titleFallback: config.titleFallback,
      readPayload: config.readPayload ?? readStandardExportPayload,
      buildOnPreview:
        config.buildOnPreview ?? buildOnOpenExportDocumentPreview(onOpenDocumentExport),
    },
    intl
  );

export const buildPptxOnPreview =
  (
    onOpenPresentation?: (presentationId: string) => void,
    onOpenDocumentExport?: (payload: OpenExportDocumentPayload) => void
  ) =>
  (payload: ArtefactPayload): (() => void) | undefined => {
    if (onOpenPresentation && payload.presentationId) {
      return () => {
        if (getWorkHostPort('shell').isSlideBuilderEnabled()) {
          logger.info('[SlideBuilder] projects chat opening presentation panel', {
            presentationId: payload.presentationId,
          });
          onOpenPresentation(payload.presentationId!);
          return;
        }
        const openExportPreview =
          buildOnOpenExportDocumentPreview(onOpenDocumentExport)(payload);
        if (openExportPreview) {
          openExportPreview();
          return;
        }
        logger.info('[SlideBuilder] flag off — falling back to pptx download', {
          presentationId: payload.presentationId,
        });
        if (payload.url) window.open(payload.url, '_blank', 'noopener,noreferrer');
      };
    }
    return buildOnOpenExportDocumentPreview(onOpenDocumentExport)(payload);
  };
